import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';


const ChatModal = ({ isOpen, onClose, supplier }) => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      sender: 'supplier',
      text: `Hi! I'm ${supplier?.name}. How can I help you with your order today?`,
      timestamp: new Date(Date.now() - 300000),
      type: 'text'
    }
  ]);
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = () => {
    if (!newMessage?.trim()) return;

    const userMessage = {
      id: messages?.length + 1,
      sender: 'customer',
      text: newMessage,
      timestamp: new Date(),
      type: 'text'
    };

    setMessages([...messages, userMessage]);
    setNewMessage('');
    setIsTyping(true);

    setTimeout(() => {
      const supplierResponse = {
        id: messages?.length + 2,
        sender: 'supplier',
        text: "Thank you for your message! I\'ll get back to you shortly with the details.",
        timestamp: new Date(),
        type: 'text'
      };
      setMessages(prev => [...prev, supplierResponse]);
      setIsTyping(false);
    }, 2000);
  };

  const handleFileUpload = (event) => {
    const file = event?.target?.files?.[0];
    if (file) {
      const fileMessage = {
        id: messages?.length + 1,
        sender: 'customer',
        text: 'Shared an image',
        timestamp: new Date(),
        type: 'image',
        imageUrl: URL.createObjectURL(file),
        imageAlt: 'Customer uploaded image for custom order reference'
      };
      setMessages([...messages, fileMessage]);
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp)?.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-modal flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-warm-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full overflow-hidden">
              <Image
                src={supplier?.profileImage}
                alt={supplier?.profileImageAlt}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-lg">{supplier?.name}</h3>
              <div className="flex items-center gap-2 text-sm text-success">
                <div className="w-2 h-2 bg-success rounded-full" />
                <span>Online</span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-lg hover:bg-muted transition-smooth flex items-center justify-center"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
          {messages?.map((message) => (
            <div
              key={message?.id}
              className={`flex gap-3 ${
                message?.sender === 'customer' ? 'flex-row-reverse' : 'flex-row'
              }`}
            >
              <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                <Image
                  src={
                    message?.sender === 'supplier'
                      ? supplier?.profileImage
                      : 'https://randomuser.me/api/portraits/men/75.jpg'
                  }
                  alt={
                    message?.sender === 'supplier'
                      ? supplier?.profileImageAlt
                      : 'Customer profile picture showing professional headshot'
                  }
                  className="w-full h-full object-cover"
                />
              </div>

              <div
                className={`flex-1 max-w-[70%] ${
                  message?.sender === 'customer' ? 'items-end' : 'items-start'
                } flex flex-col gap-1`}
              >
                {message?.type === 'text' ? (
                  <div
                    className={`px-4 py-3 rounded-lg ${
                      message?.sender === 'customer' ?'bg-primary text-primary-foreground' :'bg-muted'
                    }`}
                  >
                    <p className="text-sm leading-relaxed">{message?.text}</p>
                  </div>
                ) : (
                  <div className="rounded-lg overflow-hidden shadow-warm">
                    <Image
                      src={message?.imageUrl}
                      alt={message?.imageAlt}
                      className="w-full h-48 object-cover"
                    />
                    <div className="p-3 bg-muted">
                      <p className="text-sm">{message?.text}</p>
                    </div>
                  </div>
                )}
                <span className="text-xs text-muted-foreground px-2">
                  {formatTime(message?.timestamp)}
                </span>
              </div>
            </div>
          ))}

          {isTyping && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                <Image
                  src={supplier?.profileImage}
                  alt={supplier?.profileImageAlt}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="px-4 py-3 bg-muted rounded-lg">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" />
                  <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                  <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <div className="p-4 md:p-6 border-t border-border">
          <div className="flex gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef?.current?.click()}
              className="w-10 h-10 rounded-lg hover:bg-muted transition-smooth flex items-center justify-center flex-shrink-0"
            >
              <Icon name="Paperclip" size={20} />
            </button>
            <Input
              type="text"
              placeholder="Type your message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e?.target?.value)}
              onKeyPress={(e) => e?.key === 'Enter' && handleSendMessage()}
              className="flex-1"
            />
            <Button
              variant="default"
              size="icon"
              onClick={handleSendMessage}
              disabled={!newMessage?.trim()}
            >
              <Icon name="Send" size={20} />
            </Button>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <button className="px-3 py-1.5 bg-muted hover:bg-muted/80 rounded-lg text-sm transition-smooth">
              Custom cake order
            </button>
            <button className="px-3 py-1.5 bg-muted hover:bg-muted/80 rounded-lg text-sm transition-smooth">
              Dietary requirements
            </button>
            <button className="px-3 py-1.5 bg-muted hover:bg-muted/80 rounded-lg text-sm transition-smooth">
              Delivery options
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatModal;